import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/cn'

export type ChainTone = 'ok' | 'warn' | 'bad' | 'info' | 'none'

export interface ChainLink {
  key: string
  label: string
  /** Мелкий текст рядом с подписью: номер, кабинет. */
  sub?: string
  /** Точка статуса слева. */
  tone?: ChainTone
  /** Окно сейчас показывает именно это звено. */
  current?: boolean
  disabled?: boolean
  title?: string
  onClick?: () => void
}

/** Пункт меню выбора кабинета у звена площадки. */
export interface ChainMenuItem {
  key: string
  label: string
  /** Мелкий текст справа: состояние, внешний ID. */
  sub?: string
  tone?: ChainTone
  /** Этот пункт соответствует открытому окну. */
  current?: boolean
  disabled?: boolean
  title?: string
  onClick?: () => void
}

export interface ChainMarketplace {
  key: string
  label: string
  tone: ChainTone
  current?: boolean
  disabled?: boolean
  title?: string
  onClick?: () => void
  /**
   * Несколько кабинетов одной площадки: клик открывает меню выбора, а не
   * переходит сразу. Один пункт или пустой список — обычная кнопка.
   */
  menu?: ChainMenuItem[]
}

export interface ChainMarketplaces {
  key: string
  kind: 'marketplaces'
  items: ChainMarketplace[]
}

export type ChainItem = ChainLink | ChainMarketplaces

export interface EntityChainProps {
  items: ChainItem[]
  /** Если задан — первой стоит кнопка «Назад» (Alt+←). */
  onBack?: () => void
  className?: string
}

const dot: Record<ChainTone, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
  info: 'bg-indigo-500',
  none: 'border border-dashed border-gray-400 bg-transparent dark:border-zinc-500',
}

const isMarketplaces = (item: ChainItem): item is ChainMarketplaces =>
  (item as ChainMarketplaces).kind === 'marketplaces'

/**
 * Кнопка площадки с меню кабинетов. Меню рисуется порталом в body: лента
 * цепочки прокручивается по горизонтали и обрезала бы выпадающий список, а у
 * окна сущности есть transform, который ломает `fixed`.
 */
const MarketplaceMenuButton: React.FC<{ mp: ChainMarketplace; items: ChainMenuItem[] }> = ({ mp, items }) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = React.useId()

  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus()
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    // Esc закрывает только меню: окно под ним остаётся (перехват до обработчика окна).
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const toggle = () => {
    if (!open) {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (rect) setPosition({ top: rect.bottom + 4, left: rect.left })
    }
    setOpen((value) => !value)
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={mp.title}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-current={mp.current ? 'true' : undefined}
        onClick={toggle}
        className={cn(
          'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs',
          mp.current
            ? 'bg-indigo-50 font-semibold text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
            : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800',
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', dot[mp.tone])} />
        {mp.label}
        <ChevronDown className="h-3 w-3 text-gray-400 dark:text-zinc-500" aria-hidden="true" />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={`Кабинеты: ${mp.label}`}
            style={{ top: position.top, left: position.left }}
            className="fixed z-[120] min-w-[13rem] rounded-lg border border-gray-200 bg-white py-1 shadow-medium dark:border-zinc-700 dark:bg-zinc-900"
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                title={item.title}
                disabled={item.disabled || (!item.onClick && !item.current)}
                onClick={() => {
                  setOpen(false)
                  buttonRef.current?.focus()
                  if (!item.current) item.onClick?.()
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50',
                  item.current
                    ? 'bg-indigo-50 font-semibold text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
                    : 'text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800',
                )}
              >
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot[item.tone ?? 'none'])} />
                <span className="flex-1 truncate">{item.label}</span>
                {item.sub && (
                  <span className="shrink-0 font-mono text-[10px] text-gray-400 dark:text-zinc-500">{item.sub}</span>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

const linkClass = (current?: boolean, disabled?: boolean) =>
  cn(
    'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
    current
      ? 'border-indigo-300 bg-indigo-50 font-semibold text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800',
    disabled && 'cursor-not-allowed opacity-50 hover:border-gray-200 hover:bg-white dark:hover:bg-zinc-900',
  )

/**
 * EntityChain — строка цепочки связанных сущностей: Макет › Товар › Черновик › Карточка.
 *
 * Звено с `onClick` открывает связанное окно в том же каркасе; текущее звено
 * подсвечено и не нажимается. Звено вида `marketplaces` — компактный
 * переключатель площадок. Строка не переносится: при переполнении скроллится
 * по горизонтали без полосы.
 */
export const EntityChain: React.FC<EntityChainProps> = ({ items, onBack, className }) => (
  <nav
    aria-label="Связанные сущности"
    className={cn(
      'flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-gray-200 bg-gray-50/70 px-5 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden dark:border-zinc-800 dark:bg-zinc-950/40',
      className,
    )}
  >
    {onBack && (
      <button
        type="button"
        title="Назад (Alt+←)"
        aria-label="Назад"
        onClick={onBack}
        className="mr-1 inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Назад
      </button>
    )}
    {items.map((item, index) => (
      <React.Fragment key={item.key}>
        {index > 0 && (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300 dark:text-zinc-600" aria-hidden="true" />
        )}
        {isMarketplaces(item) ? (
          <span
            role="group"
            aria-label="Площадки"
            className="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5 dark:border-zinc-700 dark:bg-zinc-900"
          >
            {item.items.map((mp) =>
              mp.menu && mp.menu.length > 1 ? (
                <MarketplaceMenuButton key={mp.key} mp={mp} items={mp.menu} />
              ) : (
              <button
                key={mp.key}
                type="button"
                title={mp.title}
                disabled={mp.disabled || (!mp.onClick && !mp.current)}
                aria-current={mp.current ? 'true' : undefined}
                onClick={mp.current ? undefined : mp.onClick}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-50',
                  mp.current
                    ? 'bg-indigo-50 font-semibold text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800',
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', dot[mp.tone])} />
                {mp.label}
              </button>
              ),
            )}
          </span>
        ) : (
          <button
            type="button"
            title={item.title}
            disabled={item.disabled || (!item.onClick && !item.current)}
            aria-current={item.current ? 'step' : undefined}
            onClick={item.current ? undefined : item.onClick}
            className={linkClass(item.current, item.disabled)}
          >
            {item.tone && <span className={cn('h-1.5 w-1.5 rounded-full', dot[item.tone])} />}
            <span className="whitespace-nowrap">{item.label}</span>
            {item.sub && (
              <span className="whitespace-nowrap font-mono text-[10px] text-gray-400 dark:text-zinc-500">
                {item.sub}
              </span>
            )}
          </button>
        )}
      </React.Fragment>
    ))}
  </nav>
)

export default EntityChain
